import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { LinkedTableAddDialogComponent, LinkedTableAddDialogInput, LinkedTableAddDialogOutput } from 'src/app/components/linked-table-add-dialog/linked-table-add-dialog.component';
import { AppState } from '../../reducers';
import { SubCategoryCreated } from '../_actions/sub-category.actions';
import { SubTypeCategoryCreated } from '../_actions/sub-type-category.actions';
import { SubCategoryModel } from '../_models/sub-category.model';
import { SubTypeCategoryModel } from '../_models/sub-type-category.model';
import { SubCategoryService } from './sub-category.service';
import { SubTypeCategoryService } from './sub-type-categories.service';

/**
 * "New entry" option for the sub-type category / sub-category dropdowns.
 * Opens the add dialog, creates the entry, adds it to the store and selects it on the control.
 * On cancel, the control gets back the value it had before the "new" option was picked.
 */
@Injectable({ providedIn: 'root' })
export class SubCategoryEntryService {
  constructor(
    private dialog: MatDialog,
    private store: Store<AppState>,
    private subTypeCategoryService: SubTypeCategoryService,
    private subCategoryService: SubCategoryService
  ) {}

  addSubTypeCategory(control: AbstractControl, propertySubTypeId: number): Observable<SubTypeCategoryModel | null> {
    return this._add(control, 'New Sub-type Category', 'Sub-type category', res => {
      const model = new SubTypeCategoryModel();
      model.clear();
      model.name = res.name;
      model.property_sub_type_id = propertySubTypeId;
      return this.subTypeCategoryService.create(model).pipe(
        map(created => created.data as SubTypeCategoryModel),
        tap(item => this.store.dispatch(new SubTypeCategoryCreated({ item })))
      );
    });
  }

  addSubCategory(control: AbstractControl, subTypeCategoryId: number): Observable<SubCategoryModel | null> {
    return this._add(control, 'New Sub-category', 'Sub-category', res => {
      const model = new SubCategoryModel();
      model.clear();
      model.name = res.name;
      model.sub_type_category_id = subTypeCategoryId;
      return this.subCategoryService.create(model).pipe(
        map(created => created.data as SubCategoryModel),
        tap(item => this.store.dispatch(new SubCategoryCreated({ item })))
      );
    });
  }

  private _add<T extends { id: number }>(
    control: AbstractControl,
    title: string,
    placeholder: string,
    create: (res: LinkedTableAddDialogOutput) => Observable<T>
  ): Observable<T | null> {
    const previousValue = control.value;
    const dialogRef = this.dialog.open<LinkedTableAddDialogComponent, LinkedTableAddDialogInput, LinkedTableAddDialogOutput>(
      LinkedTableAddDialogComponent,
      {
        data: { title, placeholder },
        width: '400px'
      }
    );
    return dialogRef.afterClosed().pipe(
      switchMap(res => {
        if (!res) {
          control.setValue(previousValue);
          return of(null);
        }
        return create(res).pipe(
          tap(item => {
            control.setValue(item.id);
            control.updateValueAndValidity();
          })
        );
      })
    );
  }
}
